import { createRef, useEffect, useState } from "react"
import * as PropTypes from "prop-types"
import { Icon, LoadingSpinner } from "@oysterhr/ui"
import classNames from "classnames"

import PaginationControl from "./PaginationControl"
import { Transition, TransitionGroup } from "react-transition-group"

const duration = 100
const delayInterval = 30

const defaultStyles = {
  transition: `all ${duration}ms ease-in-out`,
  opacity: 0,
  boxShadow: "0px 1px 0px rgba(239, 241, 244, 0)",
  // translate works a bit buggy for flex items, will need to fix it later
  // transform: "translateY(-10px)",
}

const transitionStyles = {
  entering: {
    opacity: 0.01,
    boxShadow: "0px 1px 0px rgba(239, 241, 244, 0)",
    // transform: "translateY(-10px)",
  },
  entered: {
    opacity: 1,
    boxShadow: "0px 1px 0px rgba(239, 241, 244, 1)",
    // transform: "translateY(0px)",
  },
  exiting: {
    display: "none",
  },
}

const DEFAULT_EMPTY_MESSAGE = "Empty table"

const toggleOrder = (
  thisColumn,
  { orderColumn, setOrderColumn, orderDirection, setOrderDirection },
) => {
  if (orderColumn === thisColumn) {
    setOrderDirection(1 - orderDirection)
  } else {
    setOrderColumn(thisColumn)
    setOrderDirection(0)
  }
}

const SortIcon = ({ thisColumn, orderStates: { orderColumn, orderDirection } }) => {
  if (thisColumn === orderColumn) {
    return (
      <Icon name={orderDirection === 1 ? "SortDown" : "SortUp"} className="ml-2 text-gray-400" />
    )
  } else {
    return <Icon name="Sort" className="ml-2 text-gray-300" />
  }
}

/**
 * @typedef {Object} AnimatedTableProps
 * @property {Array<{ title: string | ReactNode, orderName?: string }>} columns -
 *   An array of column definitions. Each column can have a title and an optional orderName for sorting.
 *   If any columns have an orderName, columnOrderStates MUST also be provided.
 * @property {Array<ReactNode>} rows - An array of row data to be displayed in the table.
 * @property {boolean} [loading] - Indicates whether the table is currently loading data.
 * @property {string} [emptyMessage] - Message to display when there are no rows.

 * @property {string} [className] - Additional class name for styling the
 */

/**
 * @param {AnimatedTableProps} props
 * @returns {JSX.Element}
 */
const AnimatedTable = ({
  columns = [],
  rows = [],
  loading,
  emptyMessage,
  columnOrderStates,
  className,
  metadata,
  setPage,
  testId,
  hideOnMobile = false,
}) => {
  const [delayedRows, setDelayedRows] = useState()

  useEffect(() => {
    setDelayedRows(rows)
  }, [rows])

  const columnCount = columns.length
  const rowCount = rows.length

  return (
    <div
      className={classNames("relative w-full", {
        "hidden md:block": hideOnMobile,
      })}
    >
      {loading && <LoadingSpinner />}
      <table
        role="grid"
        className={classNames("w-full text-sm", { [className]: className })}
        data-testid={testId || ""}
      >
        <thead>
          <tr className="rounded-t-lg bg-gray-100 text-sm font-medium text-gray-400">
            {columns
              .filter(c => c)
              .map(({ title, orderName }, index) => {
                return (
                  <td
                    key={index}
                    role="gridcell"
                    className={
                      "whitespace-nowrap " +
                      (orderName ? "cursor-pointer " : "") +
                      (index === 0
                        ? "rounded-tl-md px-4 py-3"
                        : index === columnCount - 1
                          ? "rounded-tr-md px-4 py-3"
                          : "px-4 py-3")
                    }
                    onClick={() => orderName && toggleOrder(orderName, columnOrderStates)}
                  >
                    {title}
                    {orderName && (
                      <SortIcon thisColumn={orderName} orderStates={columnOrderStates} />
                    )}
                  </td>
                )
              })}
          </tr>
        </thead>
        <TransitionGroup component="tbody">
          {!loading &&
            delayedRows?.map((row, i) => {
              // FIXME: A way to overcome findDomNode is deprecated error
              // https://github.com/reactjs/react-transition-group/issues/668#issuecomment-1047110433
              const itemRef = createRef(null)
              return (
                <Transition key={row.key || i} timeout={0} nodeRef={itemRef}>
                  {state => (
                    <row.type
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...row.props}
                      style={{
                        ...defaultStyles,
                        ...transitionStyles[state],
                        transitionDelay: `${i * delayInterval}ms`,
                      }}
                    >
                      {row.props.children}
                    </row.type>
                  )}
                </Transition>
              )
            })}
        </TransitionGroup>
      </table>
      {!loading && metadata && (
        <div className="mt-4 flex justify-center">
          <PaginationControl {...metadata} setPage={setPage} />
        </div>
      )}
      {rowCount === 0 && !loading && (
        <div className="pt-10 text-center text-lg text-gray-400">
          {emptyMessage || DEFAULT_EMPTY_MESSAGE}
        </div>
      )}
    </div>
  )
}

AnimatedTable.propTypes = {
  // orderName signals that this column should be sortable. If any columns have this set then
  // columnOrderStates MUST also be provided.
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      orderName: PropTypes.string,
    }),
  ).isRequired,

  rows: PropTypes.arrayOf(PropTypes.node).isRequired,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,

  // Table will update these states when the user clicks to change the sort column or direction.
  columnOrderStates: PropTypes.shape({
    orderColumn: PropTypes.string,
    orderDirection: PropTypes.number,
    setOrderColumn: PropTypes.func,
    setOrderDirection: PropTypes.func,
  }),

  className: PropTypes.string,

  // Metadata for pagination
  metadata: PropTypes.object,
  // Function to set page number to global context
  setPage: PropTypes.func,
  // data-testid to be added to table
  testId: PropTypes.string,
}

export default AnimatedTable
